import { FactCheck } from '@mui/icons-material';
import { ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { QuestionWarningIcon } from '../components/questionWarnings';
import ProgramQuestionModal from './programQuestionModal';

import { useProgramQuestions } from 'hooks/useProgramQuestions';
import useTranslate from 'hooks/useTranslate';
import { initialAnswers, questionWarningCount } from 'views/v2/programQuestions/formState';
import {
    EXPRESS_ENTRY_PROGRAM_ID,
    isCanadaExpressEntry,
    isCanadaPreEligibility,
    isCanadaIec,
} from 'views/v2/programQuestions/visibility';

interface ProgramQuestionSidebarStepProps {
    collapsed: boolean;
    userId?: number;
    country?: string | null;
    programName?: string | null;
}

const ProgramQuestionSidebarStep = ({
    collapsed,
    userId,
    country,
    programName,
}: ProgramQuestionSidebarStepProps) => {
    const { translate } = useTranslate();
    const [open, setOpen] = useState(false);
    const iec = isCanadaIec(country, programName);
    const expressEntry = isCanadaExpressEntry(country, programName);
    const preEligibility = isCanadaPreEligibility(country, programName);
    const visible = iec || expressEntry || preEligibility;
    const label = translate('programQuestions');
    const programId = expressEntry
        ? EXPRESS_ENTRY_PROGRAM_ID
        : preEligibility
          ? programName?.trim()
          : null;
    const questionsQuery = useProgramQuestions(
        'Canada',
        userId,
        'program-question',
        programId,
        visible
    );
    const warningCount = useMemo(() => {
        const questions = questionsQuery.data?.questions;
        return questions ? questionWarningCount(questions, initialAnswers(questions)) : 0;
    }, [questionsQuery.data]);

    useEffect(() => {
        setOpen(false);
    }, [country, programId, visible]);

    if (!visible) {
        return null;
    }

    return (
        <>
            <ListItem disablePadding>
                <Tooltip title={collapsed && warningCount === 0 ? label : ''} placement="right">
                    <ListItemButton
                        aria-label={label}
                        onClick={() => setOpen(true)}
                        sx={{
                            borderRadius: collapsed ? 1.5 : 1,
                            mb: 0.5,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            px: 1.5,
                            py: collapsed ? 1 : 0.25,
                            '& .MuiListItemIcon-root': {
                                minWidth: collapsed ? 'auto' : 40,
                                color: 'text.secondary',
                            },
                        }}
                    >
                        <ListItemIcon>
                            {collapsed && warningCount > 0 ? (
                                <QuestionWarningIcon count={warningCount} />
                            ) : (
                                <FactCheck fontSize="small" />
                            )}
                        </ListItemIcon>
                        {!collapsed && (
                            <>
                                <ListItemText primary={label} />
                                <QuestionWarningIcon count={warningCount} />
                            </>
                        )}
                    </ListItemButton>
                </Tooltip>
            </ListItem>
            <ProgramQuestionModal
                open={open}
                country="Canada"
                programId={programId}
                userId={userId}
                onClose={() => setOpen(false)}
            />
        </>
    );
};

export default ProgramQuestionSidebarStep;
